// The quote drawer's writes outside Save (#1559): Square resync, crew, the
// event's client and Delete draft. Each toasts its own failure and resolves to
// whether it worked, so the drawer never has to catch.
import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { QuoteRow } from "@/hooks/useQuotes";
import { fetchDraftDeletable, invalidateEventViews } from "@/hooks/useEventsPipeline";
import { useQuoteServers, useSetQuoteServers } from "@/hooks/useStaff";
import { captureError, getErrorMessage } from "@/lib/error-message";
import { edgeFunctionErrorMessage } from "@/lib/edge-function-error";
import { draftUnchangedSince } from "./drawerLogic";

/** A busy flag plus a guard, so a double click runs the action once. */
export function useBusy() {
  const running = useRef(false);
  const [busy, setBusy] = useState(false);
  const run = async <T>(fallback: T, fn: () => Promise<T>): Promise<T> => {
    if (running.current) return fallback;
    running.current = true;
    setBusy(true);
    try {
      return await fn();
    } finally {
      running.current = false;
      setBusy(false);
    }
  };
  return { busy, run };
}

type ResyncPayload = { error?: string; total?: number; warnings?: { reason: string; detail?: string }[] } | null;

/** Resync from Square: the old panel's handleSquareResync, unchanged. */
export function useSquareResync(quoteId: string) {
  const qc = useQueryClient();
  const { busy, run } = useBusy();
  const resync = () =>
    run(false, async () => {
      try {
        const { data, error } = await supabase.functions.invoke("square-payments", {
          body: { action: "resync-quote", quote_id: quoteId },
        });
        if (error) throw error;
        const payload = data as ResyncPayload;
        if (payload?.error) throw new Error(payload.error);
        toast.success(`Resynced from Square — total $${Number(payload?.total ?? 0).toFixed(2)}`);
        const mismatch = payload?.warnings?.find((w) => w.reason === "totals_mismatch");
        if (mismatch) toast.warning(mismatch.detail ?? "These figures don't add up to Square's total.");
        await qc.invalidateQueries({ queryKey: ["quotes"] });
        await qc.invalidateQueries({ queryKey: ["quote_payments", quoteId] });
        return true;
      } catch (err) {
        const msg = await edgeFunctionErrorMessage(err, "Resync failed");
        captureError(err, "quote_square_resync", msg);
        toast.error(msg);
        return false;
      }
    });
  return { resync, resyncing: busy };
}

/** The driver, saved as soon as it changes (no Save button for crew). */
export function useSetQuoteDriver(quoteId: string) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { busy, run } = useBusy();
  const setDriver = (driverId: string | null) =>
    run(false, async () => {
      const { error } = await supabase.from("quotes").update({ driver_id: driverId, updated_by: user?.id }).eq("id", quoteId);
      if (error) {
        toast.error(getErrorMessage(error, "Couldn't save the driver"));
        return false;
      }
      qc.invalidateQueries({ queryKey: ["quotes"] });
      invalidateEventViews(qc);
      return true;
    });
  return { setDriver, saving: busy };
}

/**
 * The quote's servers. useSetQuoteServers deletes whatever the target set
 * omits, so nothing is written until the current servers have loaded, and not
 * while that read is in error, even over cached data (#670).
 *
 * A save shows at once: serverIds is the set just saved until the refetch
 * lands, and saving stays true while that refetch runs, so a second toggle
 * never starts from the list before the first one.
 */
export function useQuoteServersEditor(quoteId: string | null) {
  const query = useQuoteServers(quoteId);
  const setServers = useSetQuoteServers();
  const ready = query.isSuccess && !query.isError;
  // The saved set, and the read it was made over; a newer read replaces it.
  const [staged, setStaged] = useState<{ ids: string[]; at: number } | null>(null);
  const serverIds = staged && staged.at === query.dataUpdatedAt ? staged.ids : (query.data ?? []).map((r) => r.staff_id);
  const save = async (staffIds: string[]): Promise<boolean> => {
    if (!quoteId || !ready) return false;
    setStaged({ ids: staffIds, at: query.dataUpdatedAt });
    try {
      await setServers.mutateAsync({ quoteId, staffIds });
      return true;
    } catch {
      // useSetQuoteServers already toasted.
      setStaged(null);
      return false;
    }
  };
  return { query, serverIds, ready, save, saving: setServers.isPending || query.isFetching };
}

/**
 * Change client: the event's contact_id and its non-declined quotes'
 * contact_id (the #1428 sync's set). Name, email and phone go through
 * useUpdateEventDetails with the rest of Edit details. The sync never copies
 * a blank, so an email or phone the new contact lacks (`cleared`) is nulled
 * on those quotes here.
 */
export function useChangeEventClient() {
  const qc = useQueryClient();
  const { busy, run: guard } = useBusy();
  const run = ({ eventId, contactId, cleared = {} }: {
    eventId: string; contactId: string | null; cleared?: Partial<Record<"client_email" | "client_phone", null>>;
  }) =>
    guard(false, async () => {
      const ev = await supabase.from("events").update({ contact_id: contactId }).eq("id", eventId);
      const qs = ev.error
        ? ev
        : await supabase.from("quotes").update({ contact_id: contactId, ...cleared }).eq("event_id", eventId).neq("status", "declined");
      qc.invalidateQueries({ queryKey: ["quotes"] });
      invalidateEventViews(qc);
      if (qs.error) {
        toast.error(getErrorMessage(qs.error, "Couldn't change the client"));
        return false;
      }
      return true;
    });
  return { run, pending: busy };
}

type DraftShown = { itemCount: number; total: number };

/** The checks before Delete draft: still deletable, unchanged since the confirm. Null means go ahead. */
async function deleteRefusal(quote: QuoteRow, shown: DraftShown): Promise<string | null> {
  const no = quote.quote_number;
  if (!(await fetchDraftDeletable(quote.event_id, quote.id))) return `${no} is the event's only quote, so it was kept`;
  const { data, error } = await supabase
    .from("quotes")
    .select("status, total, amount_paid, line_items:quote_line_items(id)")
    .eq("id", quote.id)
    .single();
  if (error) throw error;
  if (!data || !draftUnchangedSince(shown, data)) return `${no} has changed, so it was kept`;
  return null;
}

/**
 * Delete draft, with its own delete filtered on status = 'draft', so the
 * server deletes the row only while it is still a draft. Runs
 * useDeleteQuote's invalidations, then onDeleted.
 */
export function useDeleteDraft(quote: QuoteRow, onDeleted: () => void) {
  const qc = useQueryClient();
  const { busy, run } = useBusy();
  const deleteDraft = (shown: DraftShown) =>
    run(false, async () => {
      try {
        const refusal = await deleteRefusal(quote, shown);
        if (refusal) {
          toast.error(refusal);
          return false;
        }
        const { data, error } = await supabase.from("quotes").delete().eq("id", quote.id).eq("status", "draft").select("id");
        if (error) throw error;
        if (!data?.length) {
          toast.error(`${quote.quote_number} is no longer a draft, so it was kept`);
          return false;
        }
      } catch (err) {
        toast.error(getErrorMessage(err, "Couldn't check the draft, so it was kept"));
        return false;
      }
      qc.invalidateQueries({ queryKey: ["quotes"] });
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["event-detail"] });
      toast.success("Quote deleted");
      onDeleted();
      return true;
    });
  return { deleteDraft, deleting: busy };
}
